import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  UserPlus,
  Shield,
  ShieldCheck,
  Edit,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff,
  Crown,
  User,
  Mail,
  Phone
} from 'lucide-react';

const permissionLabels = {
  manage_announcements: 'Manage Announcements',
  manage_news: 'Manage News',
  manage_events: 'Manage Events',
  manage_spotlights: 'Manage Spotlights',
  manage_businesses: 'Manage Businesses',
  manage_resources: 'Manage Resources',
  manage_issues: 'Manage Issues',
  manage_safety_pins: 'Manage Safety Reports',
  manage_volunteers: 'Manage Volunteers',
  manage_messages: 'Manage Messages',
  manage_admins: 'Manage Other Admins'
};

const defaultPermissions = {
  manage_announcements: true,
  manage_news: true,
  manage_events: true,
  manage_spotlights: true,
  manage_businesses: true,
  manage_resources: true,
  manage_issues: true,
  manage_safety_pins: true,
  manage_volunteers: true,
  manage_messages: true,
  manage_admins: false
};

const AdminManagement = () => {
  const { token, user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    is_super_admin: false,
    permissions: { ...defaultPermissions }
  });

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [token]);

  const handleOpenDialog = (admin = null) => {
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        email: admin.email,
        password: '',
        name: admin.name,
        phone: admin.phone || '',
        is_super_admin: admin.is_super_admin || false,
        permissions: admin.permissions || { ...defaultPermissions }
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        email: '',
        password: '',
        name: '',
        phone: '',
        is_super_admin: false,
        permissions: { ...defaultPermissions }
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAdmin(null);
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingAdmin && !formData.password) {
      toast.error('Password is required for new admin');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        email: formData.email,
        name: formData.name,
        phone: formData.phone || null,
        is_super_admin: formData.is_super_admin,
        permissions: formData.permissions
      };
      
      if (formData.password) {
        payload.password = formData.password;
      }

      if (editingAdmin) {
        await axios.put(`${API}/admin/admins/${editingAdmin.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Admin updated successfully');
      } else {
        payload.password = formData.password;
        await axios.post(`${API}/admin/admins`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Admin created successfully');
      }
      
      handleCloseDialog();
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (admin) => {
    if (!confirm(`Are you sure you want to delete ${admin.name}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/admins/${admin.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin deleted successfully');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const handlePermissionToggle = (key) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key]
      }
    }));
  };

  const handleSuperAdminToggle = (checked) => {
    setFormData(prev => ({
      ...prev,
      is_super_admin: checked,
      permissions: checked 
        ? Object.fromEntries(Object.keys(defaultPermissions).map(k => [k, true]))
        : prev.permissions
    }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <RefreshCw className="w-8 h-8 text-plp-blue animate-spin" />
      </div>
    );
  }

  // Check if current user can manage admins
  const canManageAdmins = currentUser?.is_super_admin || currentUser?.permissions?.manage_admins;

  if (!canManageAdmins) {
    return (
      <div className="p-6">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-6 text-center">
            <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-800">Access Denied</h2>
            <p className="text-red-600 mt-2">You don't have permission to manage administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-plp-blue" /> Admin Management
          </h1>
          <p className="text-slate-600 mt-1">Create and manage site administrators</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()} 
          className="bg-plp-blue hover:bg-plp-blue-light"
          data-testid="create-admin-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" /> Add Admin
        </Button>
      </div>

      {/* Admins List */}
      <div className="grid gap-4">
        {admins.map((admin) => (
          <Card key={admin.id} className="bg-white" data-testid={`admin-card-${admin.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    admin.is_super_admin ? 'bg-plp-gold' : 'bg-plp-blue'
                  }`}>
                    {admin.is_super_admin ? (
                      <Crown className="w-6 h-6 text-plp-blue" />
                    ) : (
                      <User className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-semibold text-slate-900">{admin.name}</h3>
                      <Badge variant={admin.is_super_admin ? "default" : "secondary"} className={
                        admin.is_super_admin ? "bg-plp-gold text-plp-blue" : ""
                      }>
                        {admin.is_super_admin ? 'Super Admin' : 'Admin'}
                      </Badge>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {admin.email}
                      </span>
                      {admin.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {admin.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 sm:self-auto self-end">
                  {admin.id !== currentUser?.id && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDialog(admin)}
                        data-testid={`edit-admin-${admin.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(admin)}
                        data-testid={`delete-admin-${admin.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {admin.id === currentUser?.id && (
                    <Badge variant="outline">You</Badge>
                  )}
                </div>
              </div>
              
              {/* Permissions Summary */}
              {!admin.is_super_admin && admin.permissions && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-slate-500 mb-2">Permissions:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(admin.permissions).filter(([k, v]) => v && k !== 'manage_admins').map(([key]) => (
                      <Badge key={key} variant="outline" className="text-xs">
                        {permissionLabels[key]?.replace('Manage ', '')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingAdmin ? 'Edit Admin' : 'Create New Admin'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Admin name"
                  required
                  data-testid="admin-name-input"
                />
              </div>
              
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="admin@example.com"
                  required
                  data-testid="admin-email-input"
                />
              </div>
              
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                  placeholder="242-XXX-XXXX"
                  data-testid="admin-phone-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="password">
                  Password {editingAdmin ? '(leave blank to keep current)' : '*'}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                    placeholder={editingAdmin ? '••••••••' : 'Enter password'}
                    required={!editingAdmin}
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Admin Type */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className={`w-5 h-5 ${formData.is_super_admin ? 'text-plp-gold' : 'text-slate-400'}`} />
                  <div>
                    <Label className="text-base">Super Admin</Label>
                    <p className="text-xs text-slate-500">Has all permissions including managing other admins</p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_super_admin}
                  onCheckedChange={handleSuperAdminToggle}
                  data-testid="super-admin-switch"
                />
              </div>
            </div>
            
            {/* Permissions */}
            {!formData.is_super_admin && (
              <div className="space-y-3">
                <Label>Permissions</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(permissionLabels).filter(([k]) => k !== 'manage_admins').map(([key, label]) => (
                    <div
                      key={key}
                      onClick={() => handlePermissionToggle(key)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.permissions[key]
                          ? 'border-plp-blue bg-plp-blue/5'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border ${
                          formData.permissions[key]
                            ? 'bg-plp-blue border-plp-blue'
                            : 'border-slate-300'
                        } flex items-center justify-center`}>
                          {formData.permissions[key] && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm">{label.replace('Manage ', '')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-plp-blue hover:bg-plp-blue-light"
                data-testid="save-admin-btn"
              >
                {submitting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {editingAdmin ? 'Update Admin' : 'Create Admin'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManagement;
